import {defineCustomElement} from '../../common/init';
import {
  LoginWithShopSdkPageName,
  LoginWithShopSdkUserAction,
  ShopifyPayModalState,
} from '../../common/analytics/types';
import Bugsnag from '../../common/bugsnag';
import {logError} from '../../common/logging';
import {IFrameEventSource} from '../../common/MessageEventSource';
import MessageListener from '../../common/MessageListener';
import {PayMessageSender} from '../../common/MessageSender';
import {ShopSheetModal} from '../../common/shop-sheet-modal/shop-sheet-modal';
import {I18n} from '../../common/translator/i18n';
import {StoreMetadata} from '../../common/utils/types';
import {validateStorefrontOrigin} from '../../common/utils/urls';
import {
  ShopHubTopic,
  exchangeLoginCookie,
  getAnalyticsTraceId,
  getStoreMeta,
  isEmptyAttributeValue,
  removeOnePasswordPrompt,
  updateAttribute,
} from '../../common/utils';
import ConnectedWebComponent from '../../common/ConnectedWebComponent';
import {
  CompletedEventPayload,
  DiscountCodeCallback,
  DiscountAppMessageEventData as MessageEventData,
  DiscountAppProcessingStatus as ProcessingStatus,
  ShopDiscountAuthEventType,
  AuthorizeStepChangedEvent,
} from '../../types';
import {
  recordOpentel,
  TelemetryMetricId,
} from '../../dynamicImports/opentelemetry';

import {MonorailTracker} from './analytics';
import {buildAuthorizeUrl, payAuthDomain, payAuthDomainAlt} from './authorize';
import {ERRORS, LOAD_TIMEOUT_MS} from './constants';
import DiscountAuthView, {ViewState} from './view/DiscountAuthView';
import {
  Attribute,
  getDiscountAuthVersion,
  isDiscountVisible,
  isValidSaveDiscountStep,
  isValidShowDiscountStep,
  sanitizeDisclosureText,
  SaveDiscountError,
  SAVE_DISCOUNT_AT_OPTIONS,
  shouldSaveDiscount,
  SHOW_DISCOUNT_AT_OPTIONS,
  Step,
  STEP_ORDER,
  validateConfig,
  validateRequiredAttributes,
} from './utils';

const DISCOUNT_SAVED_MESSAGE_DURATION = 1500;
const DISCOUNT_SHOWN_MESSAGE_DURATION = 2000;
const SMS_CONFIRMATION_MESSAGE_DURATION = 5000;

const DEVMODE_EMAIL_NOT_A_USER = 'success-not-a-shop-user@myshopify.io';
const DEVMODE_EMAIL_BLOCKED = 'blocked-user@myshopify.io';
const DEVMODE_EMAIL_DISCOUNT_ERROR = 'discount-code-error@myshopify.io';

const DEFAULT_CONFIGS = {
  emailCapture: {
    showDiscountAt: Step.AuthorizeShown,
    saveDiscountAt: Step.AuthorizeSuccess,
  },
  phoneCapture: {
    showDiscountAt: Step.Never,
    saveDiscountAt: Step.AuthorizeSuccess,
  },
};

export default class ShopDiscountAuth extends ConnectedWebComponent {
  #iframeLoaded = false;
  #iframeReadyToShow = false;
  #userCookieExists = false;
  #email = '';
  #discountCode = '';

  #discountCodeCallback = '';
  #i18n: I18n | null = null;

  #iframe!: HTMLIFrameElement;
  #iframeListener?: MessageListener<MessageEventData>;
  #iframeMessenger?: PayMessageSender;
  #iframeLoadTimeout: ReturnType<typeof setTimeout> | undefined;

  #devMode = false;
  #hideChange: boolean;
  #analyticsTraceId = getAnalyticsTraceId();
  #storefrontOrigin = '';
  #storefrontMeta: StoreMetadata | null = null;
  #monorailTracker?: MonorailTracker;
  private _view: DiscountAuthView;
  private _phoneCapture = false;
  private _showDiscountAt: Step = DEFAULT_CONFIGS.emailCapture.showDiscountAt;
  private _saveDiscountAt: Step = DEFAULT_CONFIGS.emailCapture.saveDiscountAt;
  private _pendingStep: Step = STEP_ORDER[0];
  private _discountSaved = false;
  private _discountSaveTriggered = false;
  private _confirmCompleted = false;
  private _isModalClosing = false;
  private _phoneCaptureDisclosureText = '';
  private _apiKey = '';
  private _maskedPhoneNumber?: string;
  private _completedEventPayload: CompletedEventPayload = {
    loggedIn: false,
  };

  // Saves the header state as would be shown without personalization consent.
  private _defaultHeaderState: Partial<ViewState> = {
    title: '',
    headerDivider: true,
    discountVisible: true,
  };

  static get observedAttributes(): string[] {
    return [
      Attribute.ApiKey,
      Attribute.DevMode,
      Attribute.DiscountCodeCallback,
      Attribute.Email,
      Attribute.PhoneCapture,
      Attribute.PhoneCaptureDisclosureText,
      Attribute.SaveDiscountAt,
      Attribute.ShowDiscountAt,
      Attribute.StorefrontOrigin,
    ];
  }

  constructor() {
    super();

    if (!customElements.get('shop-sheet-modal')) {
      customElements.define('shop-sheet-modal', ShopSheetModal);
    }

    const rootElement: ShadowRoot = this.attachShadow({mode: 'open'});

    const onModalCloseRequest = () => {
      if (this._confirmCompleted || this._pendingStep === Step.AuthorizeShown) {
        this.hideModalAndDispatchCompleted();
        return;
      }

      this._view.hideModal();
      this._isModalClosing = true;
      this.#iframeMessenger?.postMessage({
        type: 'phoneshareconsentreceived',
        consented: this._pendingStep === Step.PhoneConsentConfirmed,
        ...(!this._discountSaved && {skipDiscountSaving: true}),
      });
    };
    const onOpen = () => {
      this.#iframeMessenger?.postMessage({type: 'sheetmodalopened'});
    };
    const onClose = () => {
      this.#iframeMessenger?.postMessage({
        type: 'sheetmodalclosed',
      });
      // Remove the 1password custom element from the DOM after the sheet modal is closed.
      removeOnePasswordPrompt();
    };
    this._view = new DiscountAuthView(
      rootElement,
      onModalCloseRequest,
      onOpen,
      onClose,
    );
    this._view.setModalAnalyticsTraceId(this.#analyticsTraceId);
    this.#iframe = this._view.getIframe();
    this.#hideChange = false;
  }

  get storefrontOrigin(): string {
    return this.#storefrontOrigin;
  }

  set storefrontOrigin(newStorefrontOrigin: string) {
    this.#storefrontOrigin = newStorefrontOrigin;
    this.updateAttribute(Attribute.StorefrontOrigin, newStorefrontOrigin);
  }

  get apiKey(): string {
    return this._apiKey;
  }

  set apiKey(apiKey: string) {
    this._apiKey = apiKey;
    this.updateAttribute(Attribute.ApiKey, apiKey);
  }

  async connectedCallback(): Promise<void> {
    this.#validateAttributes();
    await this.#initTranslations();

    this.#devMode = this.getBooleanAttribute(Attribute.DevMode);
    this.#hideChange = this.getAttribute(Attribute.HideChange) !== null;

    if (!this.#devMode && !this.#monorailTracker) {
      this.#monorailTracker = new MonorailTracker({
        elementName: 'shop-discount-auth',
        apiKey: this.apiKey,
        flowVersion: getDiscountAuthVersion({
          phoneCapture: this._phoneCapture,
          showDiscountAt: this._showDiscountAt,
          saveDiscountAt: this._saveDiscountAt,
        }),
        analyticsTraceId: this.#analyticsTraceId,
      });
      this._view.setMonorailTracker(this.#monorailTracker);

      this.#monorailTracker.trackPageImpression({
        page: LoginWithShopSdkPageName.SdkLoaded,
      });

      this.#monorailTracker.trackShopPayModalStateChange({
        currentState: ShopifyPayModalState.Loaded,
      });
    }

    this.#updateSrc();

    if (!this.#iframeListener) {
      /*
        If this component is used in an iframe, the message event destination will be the window object of the iframe, not the root window.
        This logic only works after the component is added to the DOM, hence it must be in the connectedCallback, not the constructor.
        */
      const eventDestination: Window | undefined =
        this.ownerDocument?.defaultView || undefined;

      const coreAuthDomain = this.#getStoreDomain();

      this.#iframeListener = new MessageListener<MessageEventData>(
        new IFrameEventSource(this.#iframe),
        [payAuthDomain, payAuthDomainAlt, coreAuthDomain],
        this.#handlePostMessage.bind(this),
        eventDestination,
      );

      this.#storefrontMeta = await getStoreMeta(coreAuthDomain);
    }

    if (!this.#iframeMessenger) {
      this.#iframeMessenger = new PayMessageSender(this.#iframe);
    }
  }

  async #initTranslations() {
    try {
      // BUILD_LOCALE is used for generating localized bundles.
      // See ./scripts/i18n-dynamic-import-replacer-rollup.mjs for more info.
      // eslint-disable-next-line no-process-env
      const locale = process.env.BUILD_LOCALE || I18n.getDefaultLanguage();
      const dictionary = await import(`./translations/${locale}.json`);
      this.#i18n = new I18n({[locale]: dictionary});
    } catch (error) {
      if (error instanceof Error) {
        Bugsnag.notify(error);
      }
    }
  }

  disconnectedCallback(): void {
    this.#iframeListener?.destroy();
  }

  attributeChangedCallback(
    name: string,
    _oldValue: string,
    newValue: string | null,
  ): void {
    // For boolean attributes only check if the attribute is passed, ignoring its value
    const attributePassed = Boolean(newValue !== null);

    switch (name) {
      case Attribute.DiscountCodeCallback:
        this._initDiscountCodeCallback();
        break;
      case Attribute.StorefrontOrigin:
        this._initStorefrontOrigin();
        break;
      case Attribute.ApiKey:
        this._initApiKey();
        this.#monorailTracker?.update({
          apiKey: this.apiKey,
        });
        break;
      case Attribute.Email:
        this.#updateEmail(newValue);
        break;
      case Attribute.PhoneCapture:
        this._initPhoneCapture(attributePassed);
        this.updateFlowVersion();
        break;
      case Attribute.ShowDiscountAt:
        this._initShowDiscountAt(newValue);
        this.updateFlowVersion();
        break;
      case Attribute.SaveDiscountAt:
        this._initSaveDiscountAt(newValue);
        this.updateFlowVersion();
        break;
      case Attribute.PhoneCaptureDisclosureText:
        this._initPhoneCaptureDisclosureText(newValue);
        break;
      case Attribute.DevMode:
        this._initDevMode();
        break;
    }
  }

  notifyEmailFieldShown(): void {
    this.#monorailTracker?.trackPageImpression({
      page: LoginWithShopSdkPageName.PartnerEmailInputShown,
    });
  }

  requestShow(email: string): void {
    if (!this.#iframeLoaded) {
      recordOpentel(TelemetryMetricId.RequestShowCalledBeforeIframeLoaded, 1, {
        component: 'shop-discount-auth',
      });
      return;
    }
    if (!email) {
      Bugsnag.notify(new Error('requestShow called without email'));
      throw new Error('Missing email string parameter');
    }

    this.#updateEmail(email);
  }

  #getStoreDomain(): string {
    return this.#storefrontOrigin || window.location.origin;
  }

  #validateAttributes(): void {
    validateRequiredAttributes(this);

    if (this.#storefrontOrigin) {
      validateStorefrontOrigin(this.#storefrontOrigin);
    }
  }

  async #requestDiscountCode(email: string): Promise<void> {
    const discountCode = await this.#getDiscountCode(email);

    if (discountCode) {
      this.#discountCode = discountCode;
      this.#displayModalIfReady();
    }
  }

  #displayModalIfReady(): void {
    if (!this.#discountCode || !this.#iframeReadyToShow) return;

    const discountVisible = isDiscountVisible(
      Step.AuthorizeShown,
      this._showDiscountAt,
    );

    this._view.updateSheetContent({
      iframeVisible: true,
      ...(discountVisible && {
        discountCode: {
          code: this.#discountCode,
        },
      }),
    });

    this._view.showModal();
  }

  #handlePayLoaded(userHasCookie: boolean, loginTitle: string) {
    this.#iframeLoaded = true;
    this.#clearLoadTimeout();

    // Save login title in case it's overwritten by the personalization consent step.
    this._defaultHeaderState.title = loginTitle;

    // eslint-disable-next-line no-warning-comments
    // TODO: Update this loginTitle per latest mockups. Consider moving it to Shop JS translation files rather than receiving it from Pay. https://github.com/Shopify/shop-identity/issues/1250
    // Once this is done, we will no longer need to save it in this._defaultHeaderState (above).
    this._view.updateSheetContent({
      title: loginTitle,
      headerVisible: userHasCookie,
    });

    this.dispatchCustomEvent(userHasCookie ? 'userfound' : 'usernotfound');
    this.#monorailTracker?.trackShopLoginFirstTimeRender();
  }

  #updateEmail(email: string | null): void {
    if (!email) return;

    try {
      validateConfig({
        isPhoneCapture: this._phoneCapture,
        saveDiscountAt: this._saveDiscountAt,
        showDiscountAt: this._showDiscountAt,
        phoneCaptureDisclosureText: this._phoneCaptureDisclosureText,
      });
    } catch (error) {
      if (error instanceof Error) {
        logError(`Invalid config. ${error.message}`);
        this.#handleError('invalid_config', error.message);
      }
      return;
    }

    this.#email = email;

    this.#monorailTracker?.trackShopPayLoginWithShopSdkUserAction({
      userAction: LoginWithShopSdkUserAction.ThirdPartyFormSubmission,
    });

    this.#requestDiscountCode(email);

    if (this.#devMode) {
      if (email === DEVMODE_EMAIL_NOT_A_USER) {
        this._updateCompletedPayload({email, loggedIn: false});
        this.#handleCompleted();
        return;
      }

      if (email === DEVMODE_EMAIL_BLOCKED) {
        this.#handleError(
          'user_blocked',
          'Hardcoded error event test email was entered.',
          email,
        );
        return;
      }
    }

    this.#iframeMessenger?.postMessage({
      type: 'emailsubmitted',
      email,
      hideChange: this.#hideChange,
    });
  }

  #updateSrc(): void {
    if (!this.#iframe) return;
    const authorizeUrl = buildAuthorizeUrl({
      analyticsTraceId: this.#analyticsTraceId,
      storefrontOrigin: this.#storefrontOrigin,
      devMode: this.#devMode,
      phoneCapture: this._phoneCapture,
      saveDiscountAt: this._saveDiscountAt,
      apiKey: this._apiKey,
      flowVersion: getDiscountAuthVersion({
        phoneCapture: this._phoneCapture,
        showDiscountAt: this._showDiscountAt,
        saveDiscountAt: this._saveDiscountAt,
      }),
    });

    this.#initLoadTimeout();
    updateAttribute(this.#iframe, 'src', authorizeUrl);
    Bugsnag.leaveBreadcrumb('Iframe url updated', {authorizeUrl}, 'state');
  }

  updateFlowVersion(): void {
    this.#monorailTracker?.update({
      flowVersion: getDiscountAuthVersion({
        phoneCapture: this._phoneCapture,
        showDiscountAt: this._showDiscountAt,
        saveDiscountAt: this._saveDiscountAt,
      }),
    });
  }

  #initLoadTimeout() {
    this.#clearLoadTimeout();
    this.#iframeLoadTimeout = setTimeout(() => {
      const {message, code} = ERRORS.temporarilyUnavailable;
      this.dispatchCustomEvent('error', {
        message,
        code,
      });
      // eslint-disable-next-line no-warning-comments
      // TODO: replace this bugsnag notify with a Observe-able event
      // Bugsnag.notify(
      //   new PayTimeoutError(`Pay failed to load within ${LOAD_TIMEOUT_MS}ms.`),
      //   {component: this.#component, src: this.iframe?.getAttribute('src')},
      // );
      this.#clearLoadTimeout();
    }, LOAD_TIMEOUT_MS);
  }

  #clearLoadTimeout() {
    if (!this.#iframeLoadTimeout) return;
    clearTimeout(this.#iframeLoadTimeout);
    this.#iframeLoadTimeout = undefined;
  }

  #handleShopUserMatched(
    userCookieExists: boolean,
    maskedPhoneNumber: string,
    personalizeConsentChallenge: boolean,
  ): void {
    this.#userCookieExists = userCookieExists;
    this._maskedPhoneNumber = maskedPhoneNumber;

    this._view.updateSheetContent({
      headerVisible: true,
      ...(personalizeConsentChallenge
        ? {
            title: this.#i18n!.translate(
              'shop_discount_auth.personalization_consent.title',
            ),
            headerDivider: false,
            discountVisible: false,
          }
        : this._defaultHeaderState),
    });

    this.#iframeReadyToShow = true;
    this.dispatchCustomEvent('shopusermatched');
    this.#displayModalIfReady();
  }

  #handleAuthorizeStepChanged({
    step,
    email,
    phone,
  }: AuthorizeStepChangedEvent): void {
    switch (step) {
      case 'one_click':
        // Resets the header to the default state - before personalization consent.
        // The "email" step is not relevant to the discount flow, the "sms" step would also fire a
        // user matched event which would update the copy. So we only care about the "one_click" step.
        this._view.updateSheetContent(this._defaultHeaderState);
        break;
      case 'email':
        this._view.updateSheetContent({
          description: this.#i18n!.translate(
            'shop_discount_auth.auth_modal.login_email_description',
            {email},
          ),
        });
        break;
      case 'sms':
        this._view.updateSheetContent({
          description: this.#i18n!.translate(
            'shop_discount_auth.auth_modal.login_sms_description',
            {phoneNumber: phone},
          ),
        });
        break;
      case 'webauthn':
        this._view.updateSheetContent({
          headerDivider: false,
          description: this.#i18n!.translate(
            'shop_discount_auth.auth_modal.login_webauthn_description',
          ),
        });
        break;
    }
  }

  #handleProcessing(
    status: ProcessingStatus,
    message: string,
    email?: string,
  ): void {
    this._view.toggleModalBusy(true);

    const isPhoneConsentStep = [
      Step.PhoneConsentConfirmed,
      Step.PhoneConsentDeclined,
    ].includes(this._pendingStep);

    // Don't show the success "Discount Saved" message on the status indicator in the Phone Capture flow.
    // This will be communicated to the user on the last screen of the flow.
    const statusIndicator =
      this._phoneCapture && status === 'success'
        ? undefined
        : {
            branded: this.#userCookieExists,
            status,
            message,
          };

    const discountVisible = isDiscountVisible(
      this._pendingStep,
      this._showDiscountAt,
    );

    /**
     * The processing indicator can be shown either in the authorization (ULC) steps, or the phone
     * consent steps. In the authorization steps the user's email is shown, so we want to keep showing
     * it while the processing indicator is visible. In the phone consent steps, the user's email is
     * no longer shown.
     */
    const showUserEmail =
      this._saveDiscountAt === Step.AuthorizeSuccess &&
      !(this._isModalClosing && this._pendingStep === Step.PhoneConsentShown) &&
      !isPhoneConsentStep;

    if (this._isModalClosing) {
      return;
    }

    this._view.updateSheetContent({
      iframeVisible: false,
      processingElementVisible: true,
      processingUserElementVisible: showUserEmail,
      phoneCaptureButtonsContainerVisible: showUserEmail,
      processingUser: email ?? this.#email,
      statusIndicator,
      discountCode: {
        ...(discountVisible && {code: this.#discountCode}),
      },
    });
  }

  #handleError(code: string, message: string, email?: string): void {
    this.#clearLoadTimeout();

    this.#monorailTracker?.trackShopPayLoginWithSdkErrorEvents({
      errorCode: code,
      errorMessage: message,
    });

    this.dispatchCustomEvent('error', {
      code,
      message,
      email,
    });

    this._view.toggleModalBusy(false);
  }

  #handleDiscountSaved() {
    // Pay saved the discount, try moving to the next step again.
    this._discountSaved = true;
    this._view.updateSheetContent({
      discountCode: {
        saved: this._discountSaved,
      },
    });
    this.#monorailTracker?.trackPageImpression({
      page: LoginWithShopSdkPageName.DiscountSaved,
    });
    this.moveToNextStep(this._pendingStep, true);
  }

  moveToNextStep(step: Step, discountWasSaved = false) {
    this._pendingStep = step;

    if (this._discountSaveTriggered && !this._discountSaved) {
      return;
    }

    // Ensure discount is saved at the configured step before moving to the next step.
    if (
      shouldSaveDiscount(
        step,
        this._saveDiscountAt,
        this._discountSaved,
        this._discountSaveTriggered,
      )
    ) {
      // Tell Pay to save the discount. Pay should message back "discountsaved" when done,
      // which will trigger moving to the next step again.
      this._view.toggleModalBusy(true);
      this._saveDiscountWithPay();
      return;
    }

    let timeout = 0;
    if (discountWasSaved) {
      timeout = DISCOUNT_SAVED_MESSAGE_DURATION;
    }
    if (step === this._showDiscountAt) {
      timeout = DISCOUNT_SHOWN_MESSAGE_DURATION;
    }

    setTimeout(() => {
      // Go to next step
      switch (step) {
        case Step.AuthorizeShown:
          break;
        case Step.AuthorizeSuccess:
          break;
        case Step.PhoneConsentShown:
          this._view.toggleModalBusy(false);
          this._updateCompletedPayload({phoneShareConsent: false});
          this._showPhoneConsent();
          break;
        case Step.PhoneConsentConfirmed:
          if (this._saveDiscountAt !== Step.Never) {
            this._showPhoneConsentConfirmed();
          }
          break;
        case Step.PhoneConsentDeclined:
          this._showPhoneConsentDeclined();
          break;
      }
    }, timeout);
  }

  // This is called when sdk-confirm is completed from Pay/Core.
  async #handleCompleted(shouldFinalizeLogin = false): Promise<void> {
    if (this._completedEventPayload?.loggedIn && shouldFinalizeLogin) {
      exchangeLoginCookie(this.#storefrontOrigin, (error) => {
        Bugsnag.notify(new Error(error));
      });

      const {avatar, email, givenName} = this._completedEventPayload;
      this.publishToHub(ShopHubTopic.UserSessionCreate, {
        email: givenName || email,
        initial: givenName?.[0] || email?.[0] || '',
        avatar,
      });
    }

    if (this._phoneCapture) {
      if (this._isModalClosing) {
        this.hideModalAndDispatchCompleted();
        return;
      }

      if (
        this._saveDiscountAt === Step.Never &&
        this._pendingStep === Step.PhoneConsentConfirmed
      ) {
        // For the "Never" flow, show the CheckYourTexts screen after confirming
        // instead of closing the modal immediately.
        this._showPhoneConsentConfirmed();
        return;
      }

      if (
        this._saveDiscountAt !== Step.PhoneConsentConfirmed ||
        this._pendingStep === Step.PhoneConsentDeclined
      ) {
        this.moveToNextStep(this._pendingStep);
      }
    } else {
      this.#monorailTracker?.trackPageImpression({
        page: LoginWithShopSdkPageName.DiscountSaved,
      });
      setTimeout(() => {
        this.hideModalAndDispatchCompleted();
      }, DISCOUNT_SHOWN_MESSAGE_DURATION);
    }
  }

  #handleUserVerified() {
    if (this._phoneCapture) {
      this.moveToNextStep(Step.PhoneConsentShown);
    } else {
      this.moveToNextStep(Step.AuthorizeSuccess);
    }
  }

  hideModalAndDispatchCompleted(): void {
    this.#iframeListener?.destroy();
    this._view.toggleModalBusy(false);
    this._updateCompletedPayload({
      email: this._completedEventPayload.email ?? this.#email,
    });

    // eslint-disable-next-line promise/catch-or-return
    this._view.hideModal().finally(() => {
      this.dispatchCustomEvent('completed', this._completedEventPayload);
    });
  }

  #handleRestarted(): void {
    this._view.reset();
    this.#userCookieExists = false;
    this.#iframeReadyToShow = false;
    this.dispatchCustomEvent('restarted');
  }

  #handleResizeIframe(height: number, width: number): void {
    this._view.resizeIframe(height, width, () => {
      this.dispatchCustomEvent('resized', {height, width});
    });
  }

  #handleIframeCloseRequest(): void {
    if (this._view.isModalBusy()) return;
    this.hideModalAndDispatchCompleted();
  }

  #handlePostMessage(data: MessageEventData): void {
    switch (data.type) {
      case 'resize_iframe':
        this.#handleResizeIframe(data.height, data.width);
        break;
      case 'restarted':
        this.#handleRestarted();
        break;
      case 'completed': {
        this._confirmCompleted = true;
        const {shopAccountUuid, shouldFinalizeLogin, type, ...remainingData} =
          data;
        this.#monorailTracker?.update({shopAccountUuid});
        this._updateCompletedPayload(remainingData);
        this.#handleCompleted(shouldFinalizeLogin);
        break;
      }
      case 'error':
        this.#handleError(data.code, data.message, data.email);
        break;
      case 'shop_user_matched':
        this.#handleShopUserMatched(
          data.userCookieExists,
          data.maskedPhoneNumber || '',
          data.personalizeConsentChallenge || false,
        );
        break;
      case 'loaded':
        this.#handlePayLoaded(data.userFound, data.loginTitle);
        break;
      case 'authorize_step_changed':
        this.#handleAuthorizeStepChanged(data);
        break;
      case 'processing_status_updated':
        this.#handleProcessing(data.status, data.message, data.email);
        break;
      case 'discount_saved':
        this.#handleDiscountSaved();
        break;
      case 'close_requested':
        this.#handleIframeCloseRequest();
        break;
      case 'user_verified':
        this.#handleUserVerified();
        break;
    }
    Bugsnag.leaveBreadcrumb('Received postMessage', data, 'process');
  }

  async #getDiscountCode(email: string) {
    if (this.#discountCode) {
      return this.#discountCode;
    }

    try {
      if (this.#devMode && email === DEVMODE_EMAIL_DISCOUNT_ERROR) {
        throw new Error('Discount code error');
      }

      if (typeof this.#discountCodeCallback !== 'string') {
        throw new Error(
          'discount-code-callback attribute was not provided or is not a string',
        );
      }

      const discountCodeCallbackFn = (window as any)[
        this.#discountCodeCallback
      ];
      if (typeof discountCodeCallbackFn !== 'function') {
        throw new Error(
          'discount-code-callback attribute did not contain the name of a global function',
        );
      }
      const discountCode = await (
        discountCodeCallbackFn as DiscountCodeCallback
      )(email);
      if (
        typeof discountCode !== 'string' ||
        discountCode.trim().length === 0
      ) {
        throw new Error('discount code is non-string or empty');
      }

      this.#monorailTracker?.trackShopPayLoginWithSdkDiscountStatus({
        discountCode,
      });

      return discountCode;
    } catch (error) {
      if (error instanceof Error) {
        this.#handleError('no_discount_received', error.message, email);
      }
    }
    return undefined;
  }

  protected dispatchCustomEvent(
    type: ShopDiscountAuthEventType,
    detail?: any,
  ): void {
    super.dispatchCustomEvent(type, detail);
  }

  private _initDiscountCodeCallback(): void {
    const discountCodeCallback = this.getAttribute(
      Attribute.DiscountCodeCallback,
    );
    if (discountCodeCallback) {
      this.#discountCodeCallback = discountCodeCallback;
    }
  }

  private _initStorefrontOrigin(): void {
    const storefrontOrigin = this.getAttribute(Attribute.StorefrontOrigin);
    if (storefrontOrigin) {
      this.storefrontOrigin = storefrontOrigin;
    }
  }

  private _initApiKey() {
    // we require this attr, the validation that it's provided will have already happened
    this.apiKey = this.getAttribute(Attribute.ApiKey)!;
    this.#updateSrc();
  }

  private _initPhoneCapture(newValue: boolean) {
    this._phoneCapture = newValue;
    this._initShowDiscountAt(this.getAttribute(Attribute.ShowDiscountAt));
    this._initSaveDiscountAt(this.getAttribute(Attribute.SaveDiscountAt));
    this.#updateSrc();
  }

  private _initShowDiscountAt(newValue: string | null) {
    if (isValidShowDiscountStep(newValue)) {
      this._showDiscountAt = newValue as Step;
      this.#updateSrc();
      return;
    }

    // Use default
    this._showDiscountAt = this._phoneCapture
      ? DEFAULT_CONFIGS.phoneCapture.showDiscountAt
      : DEFAULT_CONFIGS.emailCapture.showDiscountAt;
    this.#updateSrc();

    if (!isEmptyAttributeValue(newValue)) {
      logError(
        `Invalid value of ${
          Attribute.ShowDiscountAt
        } ('${newValue}'). Must be one of: ${SHOW_DISCOUNT_AT_OPTIONS.join(
          ', ',
        )}. Using the default '${this._showDiscountAt}' instead.`,
      );
    }
  }

  private _initSaveDiscountAt(newValue: string | null) {
    if (isValidSaveDiscountStep(newValue)) {
      this._saveDiscountAt = newValue as Step;
      this.#updateSrc();
      return;
    }

    // Use default
    this._saveDiscountAt = this._phoneCapture
      ? DEFAULT_CONFIGS.phoneCapture.saveDiscountAt
      : DEFAULT_CONFIGS.emailCapture.saveDiscountAt;
    this.#updateSrc();

    if (!isEmptyAttributeValue(newValue)) {
      logError(
        `Invalid value of ${
          Attribute.SaveDiscountAt
        } ('${newValue}'). Must be one of: ${SAVE_DISCOUNT_AT_OPTIONS.join(
          ', ',
        )}. Using the default '${this._saveDiscountAt}' instead.`,
      );
    }
  }

  private _initPhoneCaptureDisclosureText(newValue: string | null) {
    try {
      this._phoneCaptureDisclosureText = newValue
        ? sanitizeDisclosureText(newValue)
        : '';
    } catch (error) {
      if (error instanceof Error) {
        Bugsnag.notify(error);
        this.#handleError('disclosure_parse_error', error.message);
      }
    }
  }

  private _initDevMode() {
    this.#devMode = this.getBooleanAttribute(Attribute.DevMode);
    this.#updateSrc();
  }

  private async _showPhoneConsent() {
    const isDiscountSaved = this._saveDiscountAt === Step.AuthorizeSuccess;
    const title = this.#i18n!.translate(
      isDiscountSaved
        ? 'shop_discount_auth.phone_capture.consent.title_discount_saved'
        : 'shop_discount_auth.phone_capture.consent.title_discount_not_saved',
    );

    const merchantName = this.#storefrontMeta?.name || '';
    const disclosureHeading = this.#i18n!.translate(
      'shop_discount_auth.phone_capture.consent.disclosure_heading',
      {merchantName},
    );

    const confirmButtonLabel = this.#i18n!.translate(
      'shop_discount_auth.phone_capture.consent.confirm_button',
      {
        phone: this._maskedPhoneNumber,
      },
    );
    const declineButtonLabel = this.#i18n!.translate(
      'shop_discount_auth.phone_capture.consent.decline_button',
    );

    const discountVisible = isDiscountVisible(
      Step.PhoneConsentShown,
      this._showDiscountAt,
    );
    this._view.showPhoneConsentScreen({
      title,
      disclosureHeading,
      disclosureText: this._phoneCaptureDisclosureText,
      confirmButtonLabel,
      declineButtonLabel,
      onConfirm: () => {
        this._pendingStep = Step.PhoneConsentConfirmed;
        this.#iframeMessenger?.postMessage({
          type: 'phoneshareconsentreceived',
          consented: true,
        });
        this._updateCompletedPayload({phoneShareConsent: true});
        if (this._saveDiscountAt === Step.PhoneConsentConfirmed) {
          this._view.toggleModalBusy(true);
          this._saveDiscountWithPay();
        }
      },
      onDecline: () => {
        this._pendingStep = Step.PhoneConsentDeclined;
        this.#iframeMessenger?.postMessage({
          type: 'phoneshareconsentreceived',
          consented: false,
          ...(!this._discountSaved && {skipDiscountSaving: true}),
        });
        this._updateCompletedPayload({phoneShareConsent: false});
      },
      ...(discountVisible && {
        discountCode: {
          code: this.#discountCode,
        },
      }),
    });
  }

  private _showPhoneConsentConfirmed() {
    const title = this.#i18n!.translate(
      'shop_discount_auth.phone_capture.consent_confirmed.title',
    );
    const description = this.#i18n!.translate(
      'shop_discount_auth.phone_capture.consent_confirmed.description',
      {
        phone: this._maskedPhoneNumber,
      },
    );
    this._showTitleAndDescriptionOnly(title!, description!);

    this.#monorailTracker?.trackPageImpression({
      page: LoginWithShopSdkPageName.PhoneConsentConfirmed,
    });
  }

  private _showPhoneConsentDeclined() {
    if (this._saveDiscountAt !== Step.AuthorizeSuccess) {
      // Discount was never saved, so we can't tell the user it will be
      // automatically applied. Just close the modal then.

      // eslint-disable-next-line no-warning-comments
      // TODO: Tell the user they won't receive SMS, but do not mention the
      // discount. See https://github.com/Shopify/shop-identity/issues/1479
      this.hideModalAndDispatchCompleted();
      return;
    }

    const merchantName = this.#storefrontMeta?.name || '';
    const title = this.#i18n!.translate(
      'shop_discount_auth.phone_capture.consent_declined.title',
      {merchantName},
    );
    const description = this.#i18n!.translate(
      'shop_discount_auth.phone_capture.consent_declined.description',
    );
    this._showTitleAndDescriptionOnly(title!, description!);

    this.#monorailTracker?.trackPageImpression({
      page: LoginWithShopSdkPageName.PhoneConsentDeclined,
    });
  }

  private _showTitleAndDescriptionOnly(title: string, description: string) {
    this._view.showTitleAndDescriptionOnlyScreen({
      title,
      description,
    });

    const discountVisible = isDiscountVisible(
      Step.CheckYourTexts,
      this._showDiscountAt,
    );
    this._view.updateSheetContent({
      iframeVisible: false,
      ...(discountVisible && {
        discountCode: {
          code: this.#discountCode,
        },
      }),
    });

    this._view.toggleModalBusy(false);

    setTimeout(() => {
      this.hideModalAndDispatchCompleted();
    }, SMS_CONFIRMATION_MESSAGE_DURATION);
  }

  private async _saveDiscountWithPay() {
    this._discountSaveTriggered = true;
    const discountCode = await this.#getDiscountCode(this.#email);
    if (!discountCode) {
      throw new SaveDiscountError(
        'Tried to save a discount code, but no discount code received',
      );
    }

    this.#iframeMessenger?.postMessage({
      type: 'savediscount',
      discountCode,
    });
  }

  private _updateCompletedPayload(update: Partial<CompletedEventPayload>) {
    this._completedEventPayload = {
      ...this._completedEventPayload,
      ...update,
    };
  }
}

/**
 * Define the shop-discount-auth custom element.
 */
export function defineElement() {
  defineCustomElement('shop-discount-auth', ShopDiscountAuth);
}
